export type TimeString = string; // Format: HH:mm (24-hour)
export type DateString = string; // Format: yyyy-MM-dd

const PM_HOUR_OFFSET = 12;
const HOUR_DIGITS = 2;

/**
 * Converts the given custom 12-hour time from the API to 24-hour format.
 * Assumes format is `hh:mm AM` or `hh:mm PM` (2-digits, space included).
 */
export const convertTo24HourTime = (time: string): TimeString => {
  const [hoursMinutes, amPm] = time.split(' ');
  const [hours, minutes] = hoursMinutes.split(':');
  let hours24 = parseInt(hours, 10);
  if (amPm === 'PM') hours24 += PM_HOUR_OFFSET;
  if (hours === '12') hours24 -= PM_HOUR_OFFSET;
  return `${hours24
    .toFixed(0)
    .padStart(HOUR_DIGITS, '0')}:${minutes}` as TimeString;
};

/**
 * Converts the given inputs into a usable timestamp, in milliseconds.
 * @param date `yyyy-MM-dd` format.
 * @param time24 24-hour `HH:mm` format from `convertTo24HourTime`.
 * @param timeZoneOffset `-HH:mm` or `+HH:mm` format.
 */
export const convert24HourToTimestampMs = (
  date: DateString,
  time24: TimeString,
  timeZoneOffset: string
): number => new Date(`${date}T${time24}${timeZoneOffset}`).getTime();

/**
 * Converts the given inputs into a usable timestamp, in milliseconds.
 * @param date `yyyy-MM-dd` format.
 * @param time12 12-hour `hh:mm AM` or `hh:mm PM` format from the API.
 * @param timeZoneOffset `-HH:mm` or `+HH:mm` format.
 */
export const convertToTimestampMs = (
  date: DateString,
  time12: string,
  timeZoneOffset: string
): number => {
  const time24 = convertTo24HourTime(time12);
  return convert24HourToTimestampMs(date, time24, timeZoneOffset);
};

/** Whether an API time is actually null / not provided */
export const isNullTime = (time: string | undefined): time is undefined =>
  !time || time === '12:00 AM';

export interface OpenNowInputs {
  opensAt?: string;
  closesAt?: string;
  lunchStart?: string;
  lunchEnd?: string;
  timeZoneOffSet: string;
}

/**
 * Derives a boolean for whether the provided inputs correspond
 * to a location open at the given "now", based on the given sets of hours.
 * Does not consider the `closedToday` or `openingSoon` flags.
 * @param inputs Inputs to derive one to two (if lunch hours set) sets of open hours.
 * @param today Today's date in `yyyy-MM-dd` format, set as an input for unit testability.
 * @param nowMs "now", in milliseconds, from `Date.now()`, but is set as an input for unit testability.
 */
export const isOpenNow = (
  inputs: OpenNowInputs,
  today: DateString,
  nowMs: number
): boolean => {
  const { timeZoneOffSet, opensAt, closesAt, lunchStart, lunchEnd } = inputs;
  // Defensive programming, in case one of the required inputs is missing
  if (isNullTime(opensAt) || isNullTime(closesAt)) {
    return false;
  }

  const opensAtMs = convertToTimestampMs(today, opensAt, timeZoneOffSet);
  const closesAtMs = convertToTimestampMs(today, closesAt, timeZoneOffSet);
  if (isNullTime(lunchStart) || isNullTime(lunchEnd)) {
    return nowMs >= opensAtMs && nowMs < closesAtMs;
  }

  // Carve out lunch hour if set
  const lunchStartMs = convertToTimestampMs(today, lunchStart, timeZoneOffSet);
  const lunchEndMs = convertToTimestampMs(today, lunchEnd, timeZoneOffSet);
  return (
    (nowMs >= opensAtMs && nowMs < lunchStartMs) ||
    (nowMs >= lunchEndMs && nowMs < closesAtMs)
  );
};
